/**
 * @file    validation.ts
 * @scope   SHARED
 * @summary CPR save-guard: mirrors the FrontStack fail-loud rules (docs/guardrails.md).
 * @consumes CprSlice, CPR constants, programsManagerModel
 * @produces getCprValidationErrors, isCprInvalid, CprValidationError
 * @sideEffects None (pure).
 * @notes   Blocks saving when the backend would reject the widget, so errors surface in the drawer
 *          instead of deep in Hephaestus:
 *          - `Unknown`/missing `AggregationType` → InvalidOperationException;
 *          - missing `Trend` → InvalidOperationException;
 *          - empty `QueryCollections` → ArgumentException;
 *          - empty `DataCollectionGuid`/`OriginId` → rejected by Hephaestus.
 */
import { CPR_AGGREGATION_TYPES, CPR_MAX_ROLLUP_METRICS, CPR_TREND_PERIOD_TYPES } from './constants';
import { CprSlice } from './model';
import {
  hasDuplicatePrograms,
  isProgramIncomplete,
} from './Views/ProgramsManager/programsManagerModel';

export type CprValidationError =
  | 'missingAggregationType'
  | 'missingTrend'
  | 'missingMetric'
  | 'tooManyMetrics'
  | 'missingCalculations'
  | 'noContributors'
  | 'invalidContributor'
  | 'duplicateContributor';

const isKnown = (values: readonly string[], value?: string): boolean =>
  !!value && values.includes(value);

/** @returns Every rule the slice breaks; empty = savable. */
export const getCprValidationErrors = (slice: CprSlice): CprValidationError[] => {
  const errors: CprValidationError[] = [];
  if (!isKnown(CPR_AGGREGATION_TYPES, slice.aggregationType)) {
    errors.push('missingAggregationType');
  }
  if (!slice.trend || !isKnown(CPR_TREND_PERIOD_TYPES, slice.trend.timePeriodType)) {
    errors.push('missingTrend');
  }

  const metric = slice.metrics[0];
  if (!metric) {
    errors.push('missingMetric');
    return errors;
  }
  if (slice.metrics.length > CPR_MAX_ROLLUP_METRICS) errors.push('tooManyMetrics');
  if (metric.calculations.length === 0) errors.push('missingCalculations');
  if (metric.programs.length === 0) errors.push('noContributors');
  if (metric.programs.some(isProgramIncomplete)) errors.push('invalidContributor');
  if (hasDuplicatePrograms(metric.programs)) errors.push('duplicateContributor');
  return errors;
};

export const isCprInvalid = (slice: CprSlice): boolean => getCprValidationErrors(slice).length > 0;
